import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import MapView from "@/components/MapView";
import { ArrowLeft, Phone, MessageSquare, ShieldAlert, X, Star, CheckCircle2, Loader2 } from "lucide-react";

export default function RideTracking() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [showRate, setShowRate] = useState(false);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        if (!mounted) return;
        setB(data);
        if (data.status === "completed" && !data.rating) setShowRate(true);
      } catch (e) {
        // best-effort polling
      }
    }
    load();
    const i = setInterval(load, 4000);
    return () => {
      mounted = false;
      clearInterval(i);
    };
  }, [bookingId]);

  async function cancel() {
    try {
      await api.post(`/bookings/${bookingId}/cancel`);
      toast.success("Ride cancelled");
      nav("/app");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function submitRating() {
    try {
      await api.post(`/bookings/${bookingId}/rate`, { rating, feedback });
      toast.success("Thanks for your feedback");
      nav("/app");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  useRealtime((msg) => {
    if (msg.type === "notification" || msg.type === "driver_location") {
      api.get(`/bookings/${bookingId}`).then(r => setB(r.data)).catch(() => {});
    }
  });

  if (!b) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const statusLabel = {
    pending: { label: "Finding driver", color: "bg-amber-500" },
    assigned: { label: "Driver assigned", color: "bg-blue-500" },
    driver_en_route: { label: "Driver on the way", color: "bg-primary" },
    arrived: { label: "Driver arrived", color: "bg-emerald-500" },
    in_progress: { label: "On trip", color: "bg-primary" },
    completed: { label: "Completed", color: "bg-slate-700" },
    cancelled: { label: "Cancelled", color: "bg-rose-500" },
  }[b.status] || { label: b.status, color: "bg-slate-500" };

  const driverLat = b.trip?.current_lat || b.driver?.current_lat;
  const driverLng = b.trip?.current_lng || b.driver?.current_lng;

  return (
    <div className="relative min-h-screen bg-slate-50">
      <div className="absolute inset-0 z-0 h-[55vh]">
        <MapView
          pickup={{ lat: b.pickup_lat, lng: b.pickup_lng }}
          dropoff={{ lat: b.dropoff_lat, lng: b.dropoff_lng }}
          driver={driverLat ? { lat: driverLat, lng: driverLng } : null}
          route={[[b.pickup_lat, b.pickup_lng], [b.dropoff_lat, b.dropoff_lng]]}
          center={[(b.pickup_lat + b.dropoff_lat) / 2, (b.pickup_lng + b.dropoff_lng) / 2]}
          zoom={11}
        />
      </div>

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-4 pt-safe pt-6 z-10">
        <div className="flex items-center justify-between">
          <button onClick={() => nav("/app")} className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center" data-testid="tracking-back">
            <ArrowLeft size={20} />
          </button>
          <div className="bg-white px-3 py-2 rounded-full shadow flex items-center gap-2" data-testid="tracking-status">
            <span className={`w-2 h-2 rounded-full ${statusLabel.color} animate-pulse`} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{statusLabel.label}</span>
          </div>
          <button className="w-10 h-10 rounded-full bg-rose-50 text-rose-600 shadow flex items-center justify-center" data-testid="sos-btn" onClick={() => toast.warning("SOS triggered — ENEC security notified")}>
            <ShieldAlert size={18} />
          </button>
        </div>
      </div>

      {/* Bottom sheet */}
      <div className="absolute bottom-0 left-0 right-0 z-10 sheet-enter">
        <div className="bg-white rounded-t-[28px] shadow-2xl px-5 pt-5 pb-safe">
          <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-4" />

          {b.driver ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 shrink-0 rounded-full bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-display font-bold text-xl">
                  {b.driver.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-slate-900 truncate">{b.driver.name}</p>
                  <p className="text-xs text-slate-500 font-mono truncate">
                    {b.vehicle?.plate_number} · {b.vehicle?.make} {b.vehicle?.model}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Star size={12} className="fill-amber-400 text-amber-400" />
                    <span className="text-xs font-bold text-slate-700">{b.driver.rating || "5.0"}</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a href={`tel:${b.driver.phone}`} data-testid="call-driver" className="w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center">
                    <Phone size={18} />
                  </a>
                  <button className="w-11 h-11 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center">
                    <MessageSquare size={18} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <Loader2 size={28} className="animate-spin text-primary mx-auto" />
              <p className="font-display font-bold mt-2 text-slate-900">Finding the perfect driver…</p>
              <p className="text-xs text-slate-500">Usually less than 30 seconds</p>
            </div>
          )}

          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-start gap-3 py-1">
              <div className="w-2 h-2 rounded-full bg-slate-900 ring-4 ring-slate-200 mt-1.5" />
              <span className="flex-1 text-slate-700 truncate">{b.pickup_address}</span>
            </div>
            <div className="flex items-start gap-3 py-1">
              <div className="w-2 h-2 rounded-full bg-primary ring-4 ring-primary-200 mt-1.5" />
              <span className="flex-1 text-slate-700 truncate">{b.dropoff_address}</span>
            </div>
          </div>

          {["pending", "assigned", "driver_en_route", "arrived"].includes(b.status) && (
            <button
              data-testid="cancel-booking"
              onClick={cancel}
              className="mt-4 w-full h-12 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 font-semibold flex items-center justify-center gap-2"
            >
              <X size={18} /> Cancel Ride
            </button>
          )}

          <p className="text-center text-[10px] font-mono text-slate-400 mt-3">REF · {b.ref}</p>
        </div>
      </div>

      {/* Rating modal */}
      {showRate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 fade-up" data-testid="rate-modal">
            <div className="text-center">
              <CheckCircle2 size={56} className="text-primary mx-auto" />
              <h2 className="font-display font-bold text-2xl mt-2">Trip Completed</h2>
              <p className="text-sm text-slate-500">
                {b.trip?.distance_km?.toFixed(1) || 0} km · {b.trip?.duration_minutes || 0} min
              </p>
            </div>
            <div className="flex justify-center gap-2 mt-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`star-${n}`}
                  onClick={() => setRating(n)}
                  className="p-1"
                >
                  <Star size={36} className={n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-200"} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="feedback-text"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Any feedback for your driver? (optional)"
              className="w-full mt-4 p-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-primary text-sm"
              rows={3}
            />
            <button
              data-testid="submit-rating"
              onClick={submitRating}
              className="w-full h-12 mt-4 rounded-xl bg-primary text-white font-semibold"
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
